import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Check } from 'lucide-react';
import { notifyAPI } from '../../lib/api';

// Manager-side Notification Routing — VERTICAL-AGNOSTIC. Driven entirely by the
// server-side registries (event types + record kinds), so any vertical gets
// this UI by registering, not by new code here.
export default function NotifyRoutingPanel({ organizationId, recordKind, vertical }) {
    const { t } = useTranslation();
    const [events, setEvents] = useState([]);
    const [people, setPeople] = useState([]);
    const [records, setRecords] = useState([]);
    const [selected, setSelected] = useState([]);
    const [bulkPerson, setBulkPerson] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const [ev, pp, rr] = await Promise.all([
                notifyAPI.eventSettings(organizationId, vertical),
                notifyAPI.people(organizationId),
                notifyAPI.routing(recordKind, organizationId),
            ]);
            setEvents(ev.data?.events || []);
            setPeople(pp.data?.people || []);
            setRecords(rr.data?.records || []);
        } catch { /* keep last */ } finally { setLoading(false); }
    }, [organizationId, recordKind, vertical]);

    useEffect(() => { load(); }, [load]);

    const toggleEvent = async (key, enabled) => {
        setBusy(true);
        try { await notifyAPI.setEventSettings(organizationId, { [key]: enabled }, vertical); await load(); }
        finally { setBusy(false); }
    };

    const assign = async (recordIds, personId) => {
        setBusy(true);
        try {
            await notifyAPI.setRouting(recordKind, {
                organization_id: organizationId, record_ids: recordIds,
                notify_employee_id: personId || null,
                notify_target: personId ? 'assignee' : 'none',
            });
            await load();
        } finally { setBusy(false); }
    };

    const evLabel = (key, fallback) => t(`notify.events.${key}`, fallback);

    if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-400" /></div>;

    return (
        <div className="space-y-5" data-testid="cc-notify-routing">
            {/* ── Which events text somebody (per organization) ── */}
            <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">{t('notify.eventsTitle', 'Text my team when…')}</p>
                <div className="space-y-1.5">
                    {events.map((e) => (
                        <label key={e.key} className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-800/40 px-3 py-2.5 cursor-pointer hover:border-amber-500/40 transition-colors"
                            data-testid={`notify-event-${e.key}`}>
                            <input type="checkbox" checked={!!e.enabled} disabled={busy}
                                onChange={(ev) => toggleEvent(e.key, ev.target.checked)}
                                data-testid={`notify-event-toggle-${e.key}`}
                                className="h-4 w-4 accent-amber-500" />
                            <span className="text-sm text-slate-100 flex-1">{evLabel(e.key, e.label)}</span>
                            {e.enabled && <Check className="w-4 h-4 text-emerald-400" />}
                        </label>
                    ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">{t('notify.noPhoneExposed', 'Staff phone numbers are never shown to customers or visitors.')}</p>
            </div>

            {/* ── Record → responsible person (individual + bulk) ── */}
            <div>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{t('notify.routingTitle', 'Who gets notified')}</p>
                    <div className="flex items-center gap-2">
                        <select value={bulkPerson} onChange={(e) => setBulkPerson(e.target.value)} data-testid="notify-bulk-person"
                            className="bg-slate-900 border border-slate-700 rounded-lg text-xs text-slate-100 px-2 py-1.5">
                            <option value="">{t('notify.selectPerson', 'Select a person…')}</option>
                            {people.map((p) => <option key={p.person_id} value={p.person_id}>{p.name}{p.has_phone ? '' : ` — ${t('notify.noPhoneShort', 'no phone')}`}</option>)}
                        </select>
                        <button type="button" disabled={busy || !selected.length || !bulkPerson}
                            onClick={() => assign(selected, bulkPerson).then(() => setSelected([]))}
                            data-testid="notify-bulk-apply"
                            className="rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white text-xs font-bold px-3 py-1.5 transition-colors">
                            {t('notify.applyToSelected', 'Assign selected')} {selected.length ? `(${selected.length})` : ''}
                        </button>
                    </div>
                </div>
                <div className="max-h-80 overflow-y-auto rounded-xl border border-slate-800 divide-y divide-slate-800" data-testid="notify-routing-list">
                    {records.map((r) => (
                        <div key={r.id} className="flex items-center gap-2.5 px-3 py-2" data-testid={`notify-record-${r.code || r.id}`}>
                            <input type="checkbox" checked={selected.includes(r.id)} disabled={busy}
                                onChange={(e) => setSelected((s) => (e.target.checked ? [...s, r.id] : s.filter((x) => x !== r.id)))}
                                data-testid={`notify-record-check-${r.code || r.id}`}
                                className="h-4 w-4 accent-amber-500" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-100 truncate">{r.label}</p>
                                {r.code && <p className="text-[11px] text-slate-500">#{r.code}</p>}
                            </div>
                            <select value={r.notify_employee_id || ''} disabled={busy}
                                onChange={(e) => assign([r.id], e.target.value)}
                                data-testid={`notify-record-person-${r.code || r.id}`}
                                className="bg-slate-900 border border-slate-700 rounded-lg text-xs text-slate-100 px-2 py-1.5 max-w-[9.5rem]">
                                <option value="">{t('notify.nobody', 'Nobody')}</option>
                                {people.map((p) => <option key={p.person_id} value={p.person_id}>{p.name}</option>)}
                            </select>
                        </div>
                    ))}
                    {records.length === 0 && <p className="text-sm text-slate-400 px-3 py-4">{t('notify.noRecords', 'Nothing to route yet.')}</p>}
                </div>
            </div>

            {/* ── Team state (masked phones, opt-out + pause visibility) ── */}
            <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">{t('notify.teamTitle', 'Team notification status')}</p>
                <div className="space-y-1.5" data-testid="notify-team-list">
                    {people.map((p) => (
                        <div key={p.person_id} className="flex items-center gap-2 text-sm rounded-xl border border-slate-800 bg-slate-800/30 px-3 py-2" data-testid={`notify-person-${p.person_id}`}>
                            <span className="text-slate-100 flex-1 truncate">{p.name}</span>
                            <span className="text-[11px] text-slate-500">{p.phone_masked || t('notify.noPhoneShort', 'no phone')}</span>
                            <span className={`text-[11px] font-bold ${!p.sms_notify ? 'text-slate-400' : p.snoozed ? 'text-amber-300' : 'text-emerald-400'}`}>
                                {!p.sms_notify ? t('notify.statusOff', 'Texts off')
                                    : p.snoozed ? t('notify.statusPaused', 'Paused') : t('notify.statusOn', 'On')}
                            </span>
                        </div>
                    ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">{t('notify.personControls', 'Each person controls their own texts — you can see the state, not override it.')}</p>
            </div>
            {busy && <div className="text-[11px] text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />{t('notify.saving', 'Saving…')}</div>}
        </div>
    );
}
